import { useEffect, useState } from 'react';
import { api } from '../api/client';
import { formatXof } from '../utils/format';

const REMINDER_STATUSES = ['À envoyer', 'Envoyé', 'Relance 1', 'Relance 2', 'Réglé', 'En retard'];
const STATUS_COLOR = { 'À envoyer': 'navy', 'Envoyé': 'gold', 'Relance 1': 'orange', 'Relance 2': 'red', 'Réglé': 'green', 'En retard': 'red' };

export default function Echeanciers() {
  const [rows, setRows] = useState([]);
  const [students, setStudents] = useState([]);
  const [error, setError] = useState('');
  const [modal, setModal] = useState(null);
  const [view, setView] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');

  function load() {
    api.get('/registry/feeSchedules').then((d) =>
      setRows((Array.isArray(d) ? d : (d?.records || [])).map((r) => ({ id: r.id, ...r.data })))
    ).catch(() => {});
  }

  useEffect(() => {
    load();
    api.get('/students').then(setStudents).catch(() => {});
  }, []);

  const filtered = rows.filter((r) => !statusFilter || r.reminderStatus === statusFilter);
  const totalDu = rows.reduce((s, r) => s + Number(r.amountDue || 0), 0);
  const enRetard = rows.filter((r) => ['Relance 1', 'Relance 2', 'En retard'].includes(r.reminderStatus)).length;

  return (
    <>
      <div className="kpi-grid">
        <div className="kpi"><span>Échéanciers</span><strong>{rows.length}</strong></div>
        <div className="kpi gold"><span>Total dû</span><strong>{formatXof(totalDu)}</strong></div>
        <div className="kpi"><span>En retard</span><strong>{enRetard}</strong></div>
        <div className="kpi green"><span>Réglés</span><strong>{rows.filter((r) => r.reminderStatus === 'Réglé').length}</strong></div>
      </div>

      <div className="panel">
        <div className="panel-head">
          <h2>Échéanciers & relances</h2>
          <div style={{ display: 'flex', gap: 8 }}>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} style={{ width: 160 }}>
              <option value="">Tous statuts</option>
              {REMINDER_STATUSES.map((s) => <option key={s}>{s}</option>)}
            </select>
            <button className="btn btn-sm" onClick={() => setModal({ studentId: '', studentName: '', amountDue: '', dueDate: '', feeType: '', reminderStatus: 'À envoyer', parentContact: '', notes: '' })}>+ Ajouter</button>
          </div>
        </div>
        <div className="panel-body">
          {error && <div className="form-error">{error}</div>}
          {filtered.length === 0 ? <div className="empty">Aucun échéancier.</div> : (
            <div className="table-wrap"><table>
              <thead>
                <tr><th>Élève</th><th>Type de frais</th><th className="num">Montant dû</th><th>Échéance</th><th>Contact parent</th><th>Statut relance</th><th></th></tr>
              </thead>
              <tbody>{filtered.map((r) => (
                <tr key={r.id}>
                  <td><b>{r.studentName || '—'}</b></td>
                  <td>{r.feeType || '—'}</td>
                  <td className="num" style={{ color: 'var(--danger)', fontWeight: 700 }}>{formatXof(r.amountDue || 0)}</td>
                  <td className="mono">{r.dueDate ? new Date(r.dueDate).toLocaleDateString('fr-FR') : '—'}</td>
                  <td>{r.parentContact || '—'}</td>
                  <td><span className={`badge ${STATUS_COLOR[r.reminderStatus] || 'gray'}`}>{r.reminderStatus || '—'}</span></td>
                  <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <button className="btn-ghost btn-sm" onClick={() => setView(r)}>Fiche</button>
                    <button className="btn-ghost btn-sm" onClick={() => setModal(r)}>Modifier</button>
                  </td>
                </tr>
              ))}</tbody>
            </table></div>
          )}
        </div>
      </div>

      {modal && <EcheancierModal initial={modal} students={students} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />}
      {view && <ViewModal item={view} onClose={() => setView(null)} onEdit={() => { setModal(view); setView(null); }} fields={[['Élève', 'studentName'], ['Type de frais', 'feeType'], ['Montant dû', 'amountDue'], ['Échéance', 'dueDate'], ['Contact parent', 'parentContact'], ['Statut relance', 'reminderStatus'], ['Notes', 'notes']]} title="Echeancier" />}
    </>
  );
}

function EcheancierModal({ initial, students, onClose, onSaved }) {
  const isEdit = !!initial.id;
  const [f, setF] = useState({ ...initial });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  function onStudentChange(e) {
    const s = students.find((s) => s.id === e.target.value);
    setF((p) => ({ ...p, studentId: e.target.value, studentName: s ? `${s.lastName} ${s.firstName}` : p.studentName, parentContact: s?.ppcbPhone || p.parentContact }));
  }

  async function save() {
    if (!f.amountDue || !f.dueDate) { setError('Montant et date d\'échéance sont obligatoires.'); return; }
    setSaving(true);
    try {
      const payload = { data: f, label: f.studentName || '', searchText: `${f.studentName || ''} ${f.feeType || ''}`, refDate: f.dueDate };
      if (isEdit) await api.patch(`/registry/feeSchedules/${initial.id}`, payload);
      else await api.post('/registry/feeSchedules', payload);
      onSaved();
    } catch (e) { setError(e.message); } finally { setSaving(false); }
  }

  return (
    <div className="modal-overlay">
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h2>{isEdit ? 'Modifier l\'échéancier' : 'Nouvel échéancier'}</h2>
        {error && <div className="form-error">{error}</div>}
        <div className="field"><label>Élève</label>
          <select value={f.studentId} onChange={onStudentChange}>
            <option value="">Sélectionner…</option>
            {students.map((s) => <option key={s.id} value={s.id}>{s.lastName} {s.firstName}</option>)}
          </select>
        </div>
        <div className="field"><label>Type de frais</label><input value={f.feeType || ''} onChange={set('feeType')} placeholder="Ex : Scolarité T2" /></div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div className="field"><label>Montant dû (XOF) *</label><input type="number" min="0" value={f.amountDue || ''} onChange={set('amountDue')} /></div>
          <div className="field"><label>Date d'échéance *</label><input type="date" value={f.dueDate || ''} onChange={set('dueDate')} /></div>
        </div>
        <div className="field"><label>Contact parent</label><input value={f.parentContact || ''} onChange={set('parentContact')} /></div>
        <div className="field"><label>Statut relance</label>
          <select value={f.reminderStatus || 'À envoyer'} onChange={set('reminderStatus')}>
            {REMINDER_STATUSES.map((s) => <option key={s}>{s}</option>)}
          </select>
        </div>
        <div className="field"><label>Notes</label><textarea value={f.notes || ''} onChange={set('notes')} rows={2} /></div>
        <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
          <button className="btn" onClick={save} disabled={saving} style={{ width: 'auto', padding: '0 22px' }}>{saving ? 'Enregistrement…' : 'Enregistrer'}</button>
          <button className="btn-ghost" onClick={onClose}>Annuler</button>
        </div>
      </div>
    </div>
  );
}

function ViewModal({ item, title, fields, onClose, onEdit }) {
  if (!item) return null;
  return (
    <div className="modal-overlay">
      <div className="modal modal-wide" onClick={(e) => e.stopPropagation()}>
        <h2>Fiche — {title}</h2>
        <div className="view-grid">
          {fields.map(([label, key]) => item[key] != null && item[key] !== '' ? (
            <div key={key} className="view-row">
              <span className="view-label">{label}</span>
              <span className="view-value">{String(item[key])}</span>
            </div>
          ) : null)}
        </div>
        <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
          <button className="btn" onClick={onEdit} style={{ width: 'auto', padding: '0 22px' }}>Modifier</button>
          <button className="btn-ghost" onClick={onClose}>Fermer</button>
        </div>
      </div>
    </div>
  );
}
